import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import AdminLogin from './AdminLogin';
import TableSeating from './TableSeating';
import GiftTracker from './GiftTracker';
import { verifyToken, setAuthToken, getToken, logout } from '../utils/auth';

const AdminDashboard = () => {
  const [responses, setResponses] = useState([]);
  const [stats, setStats] = useState({
    total_responses: 0,
    total_attending: 0,
    total_declined: 0
  });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, attending, declined
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);
  const [deleteConfirm, setDeleteConfirm] = useState({ show: false, record: null });
  const [isDeleting, setIsDeleting] = useState(false);
  const [activeTab, setActiveTab] = useState('rsvp'); // 'rsvp', 'seating', or 'gifts'
  const [editingRecord, setEditingRecord] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editFormData, setEditFormData] = useState({
    name: '',
    guests: 1,
    phone: '',
    email: '',
    dietary_restrictions: '',
    message: '',
    attending: true
  });
  const [isSaving, setIsSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    checkAuth();
  }, []);

  useEffect(() => {
    if (isLoggedIn) {
      fetchData();
    }
  }, [isLoggedIn]);

  const checkAuth = async () => {
    const token = getToken();
    
    if (!token) {
      setAuthLoading(false);
      return;
    }

    const isValid = await verifyToken();
    setIsLoggedIn(isValid);
    
    if (isValid) {
      setAuthToken(token);
    }
    
    setAuthLoading(false);
  };

  const handleLogin = (status) => {
    setIsLoggedIn(status);
    if (status) {
      const token = getToken();
      setAuthToken(token);
    }
  };

  const handleLogout = async () => {
    await logout();
    setIsLoggedIn(false);
    setResponses([]);
    setStats({
      total_responses: 0,
      total_attending: 0,
      total_declined: 0
    });
  };

  const fetchData = useCallback(async () => {
    try {
      const [responsesRes, statsRes] = await Promise.all([
        axios.get('/api/rsvp'),
        axios.get('/api/rsvp/stats')
      ]);
      
      setResponses(responsesRes.data.data || []);
      setStats(statsRes.data.stats || {
        total_responses: 0,
        total_attending: 0,
        total_declined: 0
      });
    } catch (error) {
      console.error('Error fetching data:', error);
      
      // 如果是认证错误，重新登录
      if (error.response && (error.response.status === 401 || error.response.status === 403)) {
        handleLogout();
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const handleDeleteClick = (record) => {
    setDeleteConfirm({ show: true, record });
  };

  const handleDeleteCancel = () => {
    setDeleteConfirm({ show: false, record: null });
  };

  const handleEditClick = (record) => {
    setEditingRecord(record);
    setEditFormData({
      name: record.name || '',
      guests: record.guests || 1,
      phone: record.phone || '',
      email: record.email || '',
      dietary_restrictions: record.dietary_restrictions || '',
      message: record.message || '',
      attending: record.attending === 1
    });
    setShowEditModal(true);
  };

  const handleEditCancel = () => {
    setShowEditModal(false);
    setEditingRecord(null);
    setEditFormData({
      name: '',
      guests: 1,
      phone: '',
      email: '',
      dietary_restrictions: '',
      message: '',
      attending: true
    });
  };

  const handleEditChange = (field, value) => {
    setEditFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleEditSave = async () => {
    if (!editingRecord) return;
    
    // Validate required fields
    if (!editFormData.name.trim()) {
      alert('请输入姓名 Please enter a name');
      return;
    }
    
    if (!editFormData.guests || editFormData.guests < 1) {
      alert('人数必须至少为1 Guest count must be at least 1');
      return;
    }
    
    setIsSaving(true);
    try {
      const response = await axios.put(`/api/rsvp/${editingRecord.id}`, editFormData);
      
      if (response.data.success) {
        // Refresh data from server
        await fetchData();
        
        // Show success message if table assignment was removed
        if (response.data.tableAssignmentRemoved) {
          alert('记录已更新。由于出席状态改为不参加，座位分配已自动移除。\nRecord updated. Table assignment was automatically removed due to attendance change.');
        }
        
        handleEditCancel();
      } else {
        alert(response.data.error || '更新失败 Update failed');
      }
    } catch (error) {
      console.error('Error updating record:', error);
      const errorMsg = error.response?.data?.error || '更新失败，请稍后重试 Update failed, please try again';
      alert(errorMsg);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirm.record) return;
    
    setIsDeleting(true);
    try {
      await axios.delete(`/api/rsvp/${deleteConfirm.record.id}`);
      
      // Update local state
      setResponses(prevResponses => 
        prevResponses.filter(r => r.id !== deleteConfirm.record.id)
      );
      
      // Update stats
      const deletedRecord = deleteConfirm.record;
      setStats(prevStats => ({
        total_responses: prevStats.total_responses - 1,
        total_attending: deletedRecord.attending === 1 
          ? prevStats.total_attending - (deletedRecord.guests || 0)
          : prevStats.total_attending,
        total_declined: deletedRecord.attending === 0 
          ? prevStats.total_declined - 1 
          : prevStats.total_declined
      }));
      
      setDeleteConfirm({ show: false, record: null });
    } catch (error) {
      console.error('Error deleting record:', error);
      alert('删除失败，请稍后重试 Failed to delete, please try again');
    } finally {
      setIsDeleting(false);
    }
  };

  const getFilteredResponses = () => {
    let filtered = responses;
    
    // First apply attendance filter
    if (filter === 'attending') {
      filtered = filtered.filter(r => r.attending === 1);
    } else if (filter === 'declined') {
      filtered = filtered.filter(r => r.attending === 0);
    }
    
    // Then apply search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(r => {
        const name = (r.name || '').toLowerCase();
        const phone = (r.phone || '').toLowerCase();
        const email = (r.email || '').toLowerCase();
        return name.includes(query) || phone.includes(query) || email.includes(query);
      });
    }
    
    return filtered;
  };

  const filteredResponses = getFilteredResponses();

  const exportToCSV = () => {
    const headers = ['姓名', '出席状态', '人数', '手机', '邮箱', '饮食禁忌', '留言', '提交时间'];
    const csvContent = [
      headers.join(','),
      ...responses.map(r => [
        r.name,
        r.attending ? '出席' : '缺席',
        r.guests || 0,
        r.phone,
        r.email || '',
        r.dietary_restrictions || '',
        `"${(r.message || '').replace(/"/g, '""')}"`,
        new Date(r.created_at).toLocaleString('zh-CN')
      ].join(','))
    ].join('\n');

    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `wedding_rsvp_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  // 认证检查中
  if (authLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-chinese-red mx-auto"></div>
          <p className="mt-4 text-gray-600">验证登录状态...</p>
        </div>
      </div>
    );
  }

  // 未登录，显示登录页面
  if (!isLoggedIn) {
    return <AdminLogin onLogin={handleLogin} />;
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-chinese-red mx-auto"></div>
          <p className="mt-4 text-gray-600">加载数据中...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-7xl mx-auto">
        <div className="mb-6 flex justify-between items-center">
          <Link 
            to="/" 
            className="inline-flex items-center text-chinese-red hover:text-chinese-dark transition-colors"
          >
            <span className="mr-2">←</span> 返回主页 Back to Home
          </Link>
          
          <button
            onClick={handleLogout}
            className="inline-flex items-center text-gray-600 hover:text-gray-800 transition-colors bg-white px-4 py-2 rounded-lg shadow border"
          >
            <span className="mr-2">🚪</span> 安全退出 Logout
          </button>
        </div>

        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-chinese-red to-chinese-pink p-6">
            <h1 className="text-3xl font-chinese font-bold text-white">
              婚礼回复管理 RSVP Management
            </h1>
          </div>

          {/* Tab Navigation */}
          <div className="border-b border-gray-200 bg-white">
            <div className="flex">
              <button
                onClick={() => setActiveTab('rsvp')}
                className={`px-6 py-3 text-sm font-medium transition-all ${
                  activeTab === 'rsvp'
                    ? 'border-b-2 border-chinese-red text-chinese-red'
                    : 'text-gray-600 hover:text-gray-800'
                }`}
              >
                📋 RSVP列表 RSVP List
              </button>
              <button
                onClick={() => setActiveTab('seating')}
                className={`px-6 py-3 text-sm font-medium transition-all ${
                  activeTab === 'seating'
                    ? 'border-b-2 border-chinese-red text-chinese-red'
                    : 'text-gray-600 hover:text-gray-800'
                }`}
              >
                🪑 座位安排 Table Seating
              </button>
              <button
                onClick={() => setActiveTab('gifts')}
                className={`px-6 py-3 text-sm font-medium transition-all ${
                  activeTab === 'gifts'
                    ? 'border-b-2 border-chinese-red text-chinese-red'
                    : 'text-gray-600 hover:text-gray-800'
                }`}
              >
                💰 礼金记录 Gift Tracker
              </button>
            </div>
          </div>

          {/* Tab Content */}
          {activeTab === 'gifts' ? (
            /* Gift Tracker View */
            <div className="p-6">
              <GiftTracker />
            </div>
          ) : activeTab === 'rsvp' ? (
            <>
          {/* Statistics */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 p-6 bg-gray-50">
            <div className="bg-white rounded-lg p-6 shadow">
              <div className="text-3xl font-bold text-chinese-red">{stats.total_responses}</div>
              <div className="text-gray-600 mt-2">总回复数 Total Responses</div>
            </div>
            <div className="bg-white rounded-lg p-6 shadow">
              <div className="text-3xl font-bold text-green-600">{stats.total_attending}</div>
              <div className="text-gray-600 mt-2">出席人数 Attending Guests</div>
            </div>
            <div className="bg-white rounded-lg p-6 shadow">
              <div className="text-3xl font-bold text-gray-500">{stats.total_declined}</div>
              <div className="text-gray-600 mt-2">缺席人数 Declined</div>
            </div>
          </div>

          {/* Filters and Actions */}
          <div className="p-6 border-b border-gray-200 flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex gap-2">
              <button
                onClick={() => setFilter('all')}
                className={`px-4 py-2 rounded-lg transition-all ${
                  filter === 'all' 
                    ? 'bg-chinese-red text-white' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                全部 All ({responses.length})
              </button>
              <button
                onClick={() => setFilter('attending')}
                className={`px-4 py-2 rounded-lg transition-all ${
                  filter === 'attending' 
                    ? 'bg-green-600 text-white' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                出席 Attending ({responses.filter(r => r.attending === 1).length})
              </button>
              <button
                onClick={() => setFilter('declined')}
                className={`px-4 py-2 rounded-lg transition-all ${
                  filter === 'declined' 
                    ? 'bg-gray-600 text-white' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                缺席 Declined ({responses.filter(r => r.attending === 0).length})
              </button>
            </div>
            <button
              onClick={exportToCSV}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              📥 导出CSV Export CSV
            </button>
          </div>

          {/* Search Box */}
          <div className="px-6 py-4 border-b border-gray-200">
            <input
              type="text"
              placeholder="搜索宾客姓名、手机号或邮箱... Search by name, phone, or email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-chinese-red focus:border-transparent"
            />
            {searchQuery && (
              <p className="text-sm text-gray-600 mt-2">
                找到 {filteredResponses.length} 条记录 Found {filteredResponses.length} records
              </p>
            )}
          </div>

          {/* Responses Table */}
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[150px]">
                    姓名 Name
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                    状态 Status
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap w-20">
                    人数 Guests
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[180px]">
                    联系方式 Contact
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider max-w-[200px]">
                    饮食禁忌 Dietary
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider max-w-[250px]">
                    留言 Message
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap min-w-[130px]">
                    时间 Time
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap min-w-[180px]">
                    操作 Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredResponses.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="px-4 py-12 text-center text-gray-500">
                      暂无回复 No responses yet
                    </td>
                  </tr>
                ) : (
                  filteredResponses.map((response) => (
                    <tr key={response.id} className="hover:bg-gray-50">
                      <td className="px-4 py-4 min-w-[150px]">
                        <div className="text-sm font-medium text-gray-900">{response.name}</div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          response.attending 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {response.attending ? '✓ 出席' : '✗ 缺席'}
                        </span>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900 w-20">
                        {response.guests || '-'}
                      </td>
                      <td className="px-4 py-4 min-w-[180px]">
                        <div className="text-sm text-gray-900">{response.phone || '-'}</div>
                        {response.email && (
                          <div className="text-sm text-gray-500 truncate">{response.email}</div>
                        )}
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-900 max-w-[200px] truncate">
                        {response.dietary_restrictions || '-'}
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-900 max-w-[250px] truncate">
                        {response.message || '-'}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500 min-w-[130px]">
                        {new Date(response.created_at).toLocaleString('zh-CN')}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-right min-w-[180px]">
                        <button
                          onClick={() => handleEditClick(response)}
                          className="text-blue-600 hover:text-blue-900 transition-colors mr-4"
                          title="编辑记录 Edit Record"
                        >
                          ✏️ 编辑
                        </button>
                        <button
                          onClick={() => handleDeleteClick(response)}
                          className="text-red-600 hover:text-red-900 transition-colors"
                          title="删除记录 Delete Record"
                        >
                          🗑️ 删除
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
            </>
          ) : (
            /* Table Seating View */
            <div className="p-6">
              <TableSeating />
            </div>
          )}
        </div>

        {/* Delete Confirmation Modal */}
        {deleteConfirm.show && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
              <h3 className="text-lg font-semibold mb-4 text-gray-900">
                确认删除 Confirm Deletion
              </h3>
              <p className="text-gray-600 mb-6">
                您确定要删除 <strong>{deleteConfirm.record?.name}</strong> 的回复记录吗？
                <br />
                Are you sure you want to delete the response from <strong>{deleteConfirm.record?.name}</strong>?
                <br />
                <span className="text-red-600 text-sm mt-2 inline-block">
                  此操作无法撤销！This action cannot be undone!
                </span>
              </p>
              <div className="flex justify-end gap-3">
                <button
                  onClick={handleDeleteCancel}
                  disabled={isDeleting}
                  className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors disabled:opacity-50"
                >
                  取消 Cancel
                </button>
                <button
                  onClick={handleDeleteConfirm}
                  disabled={isDeleting}
                  className="px-4 py-2 text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center gap-2"
                >
                  {isDeleting ? (
                    <>
                      <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></span>
                      删除中...
                    </>
                  ) : (
                    <>🗑️ 确认删除 Delete</>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Edit RSVP Modal */}
        {showEditModal && editingRecord && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
            <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 my-8">
              <h3 className="text-xl font-semibold mb-4 text-gray-900">
                编辑回复记录 Edit RSVP Record
              </h3>
              
              <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
                {/* Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    姓名 Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={editFormData.name}
                    onChange={(e) => handleEditChange('name', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-chinese-red focus:border-transparent"
                    placeholder="请输入姓名"
                    required
                  />
                </div>

                {/* Attending Status */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    出席状态 Attending Status <span className="text-red-500">*</span>
                  </label>
                  <div className="flex gap-4">
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="radio"
                        checked={editFormData.attending === true}
                        onChange={() => handleEditChange('attending', true)}
                        className="mr-2"
                      />
                      <span className="text-sm">✓ 出席 Attending</span>
                    </label>
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="radio"
                        checked={editFormData.attending === false}
                        onChange={() => handleEditChange('attending', false)}
                        className="mr-2"
                      />
                      <span className="text-sm">✗ 不参加 Not Attending</span>
                    </label>
                  </div>
                  {editingRecord.attending === 1 && editFormData.attending === false && (
                    <p className="text-sm text-orange-600 mt-2">
                      ⚠️ 改为不参加将自动移除座位分配
                      <br />
                      Changing to "Not Attending" will automatically remove table assignment
                    </p>
                  )}
                </div>

                {/* Guest Count */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    人数 Guest Count <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={editFormData.guests}
                    onChange={(e) => handleEditChange('guests', parseInt(e.target.value) || 1)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-chinese-red focus:border-transparent"
                    required
                  />
                </div>

                {/* Phone */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    手机号码 Phone
                  </label>
                  <input
                    type="tel"
                    value={editFormData.phone}
                    onChange={(e) => handleEditChange('phone', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-chinese-red focus:border-transparent"
                    placeholder="请输入手机号"
                  />
                </div>

                {/* Email */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    电子邮箱 Email
                  </label>
                  <input
                    type="email"
                    value={editFormData.email}
                    onChange={(e) => handleEditChange('email', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-chinese-red focus:border-transparent"
                    placeholder="your@email.com"
                  />
                </div>

                {/* Dietary Restrictions */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    饮食禁忌 Dietary Restrictions
                  </label>
                  <textarea
                    value={editFormData.dietary_restrictions}
                    onChange={(e) => handleEditChange('dietary_restrictions', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-chinese-red focus:border-transparent"
                    rows="2"
                    placeholder="如：素食、清真、过敏等"
                  />
                </div>

                {/* Message */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    留言 Message
                  </label>
                  <textarea
                    value={editFormData.message}
                    onChange={(e) => handleEditChange('message', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-chinese-red focus:border-transparent"
                    rows="3"
                    placeholder="祝福留言..."
                  />
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
                <button
                  onClick={handleEditCancel}
                  disabled={isSaving}
                  className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors disabled:opacity-50"
                >
                  取消 Cancel
                </button>
                <button
                  onClick={handleEditSave}
                  disabled={isSaving}
                  className="px-4 py-2 text-white bg-chinese-red rounded-lg hover:bg-chinese-dark transition-colors disabled:opacity-50 flex items-center gap-2"
                >
                  {isSaving ? (
                    <>
                      <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></span>
                      保存中...
                    </>
                  ) : (
                    <>💾 保存 Save</>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;


